import React, { useState } from "react";
import { useAction } from "wasp/client/operations";
import { createBuySell } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import {
  ArrowLeft,
  ShoppingCart,
  DollarSign,
  Tag,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Package,
  Star,
  HandCoins,
  Mail,
} from "lucide-react";

interface BuySellFormData {
  title: string;
  description: string;
  price: string;
  category: "BUY" | "SELL";
  condition: "NEW" | "GOOD";
  phoneNumber: string;
  email: string;
  visibility: "VERIFIED" | "PENDING" | "REJECTED";
}

// Removed PRODUCT_CATEGORIES as requested

const BUY_SELL_CATEGORIES = [
  {
    value: "SELL",
    label: "Selling",
  },
  {
    value: "BUY",
    label: "Buying",
  },
];

const CONDITION_OPTIONS = [
  { value: "NEW", label: "New", description: "Brand new, never used" },
  { value: "GOOD", label: "Used", description: "In good or fair condition" },
];



// Tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function AddBuySellPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();

  const createBuySellFn = useAction(createBuySell);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<BuySellFormData>({
    title: "",
    description: "",

    price: "",
    category: "SELL",
    condition: "NEW",
    phoneNumber: "",
    email: "",
    visibility: "VERIFIED",
  });

  // Pre-fill user contact info
  React.useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
      }));
    }
  }, [user]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof BuySellFormData, value: any) => {
    let finalValue = value;
    if (field === "phoneNumber") {
      finalValue = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: finalValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createBuySellFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,

        price: formData.price ? parseFloat(formData.price) : undefined,
        category: formData.category,
        productCategory: "OTHER", // Defaulting to OTHER as field was removed

        condition: formData.condition,
        phoneNumber: formData.phoneNumber.trim(),
        email: formData.email.trim(),
        visibility: formData.visibility,
      });

      // Navigate back to the buy-sell page
      handleGoBack();
    } catch (error) {
      console.error("Error creating buy/sell item:", error);
      setErrors({ submit: "Failed to create item. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/buy-sell`);
    } else {
      navigate("/buy-sell");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-gray-600">
              You must be logged in to post buy/sell items.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="buy-sell-form"
        onClick={handleSubmit}
        disabled={isSubmitting || !formData.title.trim()}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Post Item"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>Cancel</ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Listing Category" hideOnMobile>
      <p className="text-sm text-muted-foreground">
        Select whether you are buying or selling an item.
      </p>
    </SidebarSection>
  );

  return (
    <IndividualEditPageLayout
      title="Post Item"
      subtitle="List an item for sale or request one in the marketplace"
      icon={DollarSign}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <div className="flex bg-muted p-1 rounded-lg w-fit mb-4">
              {BUY_SELL_CATEGORIES.map((category) => {
                const isActive = formData.category === category.value;
                return (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => handleChange("category", category.value)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-white text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>

      <form id="buy-sell-form" onSubmit={handleSubmit}>
        <div className="space-y-4 mb-4">
          {/* Item Title */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="What are you selling/buying?"
              className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.title
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
              }`}
              required
            />
            {errors.title && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                errors.description
                  ? "border-destructive bg-destructive/10"
                  : "border-input"
              }`}
              required
            />
            {errors.description && (
              <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
          </div>
        </div>

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Condition
              </label>
              <div className="flex bg-muted p-1 rounded-lg w-fit">
                {CONDITION_OPTIONS.map((option) => {
                  const isActive = formData.condition === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange("condition", option.value)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-white text-primary shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
 
            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center gap-2">
                  <span>Price</span>
                  <Tooltip content="Leave empty if you prefer to negotiate or if it's free">
                    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 bg-muted border border-input rounded-lg cursor-not-allowed text-muted-foreground transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleChange("phoneNumber", e.target.value)
                    }
                    className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
